import React, { useState } from 'react';
import { AACModule } from './modules/AACModule';
import { SpeechTherapyModule } from './modules/SpeechTherapyModule';
import { AutismABAModule } from './modules/AutismABAModule';
import { SignLanguageModule } from './modules/SignLanguageModule';
import { BrailleModule } from './modules/BrailleModule';
import { StudentIEPModule } from './modules/StudentIEPModule';
import { TherapistSOAPModule } from './modules/TherapistSOAPModule';
import { TeacherClassroomModule } from './modules/TeacherClassroomModule';
import { AssessmentModule } from './modules/AssessmentModule';
import { SchoolERPModule } from './modules/SchoolERPModule';
import { HospitalRehabModule } from './modules/HospitalRehabModule';
import { NDISBillingModule } from './modules/NDISBillingModule';
import { ParentCommModule } from './modules/ParentCommModule';
import { InventoryModule } from './modules/InventoryModule';
import { AnalyticsBIModule } from './modules/AnalyticsBIModule';
import { AICopilotModule } from './modules/AICopilotModule';

import { 
  Volume2, Mic, Brain, Eye, Compass, GraduationCap, Stethoscope, 
  Users, ClipboardList, Layers, Activity, CreditCard, MessageCircle, 
  Box, TrendingUp, Bot, Grid
} from 'lucide-react';

interface ModuleExplorerProps {
  initialModule?: string;
}

export const ModuleExplorer: React.FC<ModuleExplorerProps> = ({ initialModule = 'aac' }) => {
  const [activeModule, setActiveModule] = useState(initialModule);

  const modules = [
    { id: 'aac', label: 'AAC Speech Synthesizer', icon: Volume2, category: 'Communication' },
    { id: 'speech-therapy', label: 'Speech & Articulation', icon: Mic, category: 'Clinical Therapy' },
    { id: 'autism', label: 'Autism & ABA Behavior', icon: Brain, category: 'Behavioral Health' },
    { id: 'sign-language', label: 'Sign Language & Gestures', icon: Eye, category: 'Visual / Hearing' },
    { id: 'braille', label: 'Braille & Tactile Learning', icon: Compass, category: 'Visual Impairment' },
    { id: 'student', label: 'Student Profile & IEP', icon: GraduationCap, category: 'Special Education' },
    { id: 'therapist', label: 'Therapist Workspace & SOAP', icon: Stethoscope, category: 'Clinical Operations' },
    { id: 'teacher', label: 'Teacher & Classroom', icon: Users, category: 'School Education' },
    { id: 'assessment', label: 'Diagnostic Assessment', icon: ClipboardList, category: 'Evaluations' },
    { id: 'school-erp', label: 'Special School ERP', icon: Layers, category: 'Administration' },
    { id: 'hospital', label: 'Rehabilitation Hospital', icon: Activity, category: 'Inpatient Care' },
    { id: 'billing', label: 'NDIS & Insurance Billing', icon: CreditCard, category: 'Finance' },
    { id: 'communication', label: 'Parent-Therapist Chat', icon: MessageCircle, category: 'Family Engagement' },
    { id: 'inventory', label: 'Assistive Device Loans', icon: Box, category: 'Resources' },
    { id: 'analytics', label: 'Executive Analytics', icon: TrendingUp, category: 'Reporting' },
    { id: 'ai', label: 'AI Copilot Summarizer', icon: Bot, category: 'Intelligence' },
  ];

  return (
    <div className="space-y-6">
      
      {/* 16 Module Tab Switcher Strip */}
      <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex items-center gap-2 mb-2 px-2">
          <Grid className="w-4 h-4 text-blue-600" />
          <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
            16-Module Interactive Suite Launcher
          </span>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-thin">
          {modules.map((m) => {
            const Icon = m.icon;
            const isActive = activeModule === m.id;
            return (
              <button
                key={m.id}
                onClick={() => setActiveModule(m.id)}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold shrink-0 flex items-center gap-2 transition-all ${
                  isActive 
                    ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20 scale-105' 
                    : 'bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{m.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Dynamic Module Component Render */}
      <div>
        {activeModule === 'aac' && <AACModule />}
        {activeModule === 'speech-therapy' && <SpeechTherapyModule />}
        {activeModule === 'autism' && <AutismABAModule />}
        {activeModule === 'sign-language' && <SignLanguageModule />}
        {activeModule === 'braille' && <BrailleModule />}
        {activeModule === 'student' && <StudentIEPModule />}
        {activeModule === 'therapist' && <TherapistSOAPModule />}
        {activeModule === 'teacher' && <TeacherClassroomModule />}
        {activeModule === 'assessment' && <AssessmentModule />}
        {activeModule === 'school-erp' && <SchoolERPModule />}
        {activeModule === 'hospital' && <HospitalRehabModule />}
        {activeModule === 'billing' && <NDISBillingModule />}
        {activeModule === 'communication' && <ParentCommModule />}
        {activeModule === 'inventory' && <InventoryModule />}
        {activeModule === 'analytics' && <AnalyticsBIModule />}
        {activeModule === 'ai' && <AICopilotModule />}
      </div>
    </div>
  );
};
